import {useEffect, useState} from "react";
import {formatDuration} from "./publicCourseMap";

// Cross-component notification channel for the landing "added to cart" toast.
// Exported here (and consumed by CartToast + useLandingCartSync) so any cart
// action can announce itself without importing a component.
export const CART_NOTIFY_EVENT = "fillop:cart-notify";

// Landing-page cart store, following the same module-level sync pattern as
// `useWishlist`: every mounted card (grids AND horizontal rails) stays in sync
// through a shared listener set, so the same course shows the same
// already-in-cart state everywhere without prop drilling.
//
// State lives in the same localStorage key the global `CartProvider`
// (components/CardContext.jsx) persists to, so the landing cart, the cart
// drawer (CartModal) and the logged-in navbar all read one list. Adding
// through here never re-adds an existing item (keyed on id + type).

const STORAGE_KEY = "fillop_cart";
const COURSE_TYPE = "course";
const listeners = new Set();

// Broadcast whenever the cart changes so every store (landing module store AND
// the global React CartProvider) can re-read localStorage and stay in sync.
export const CART_CHANGED_EVENT = "fillop:cart-changed";

const load = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

let items = load();

const notify = () => {
  const snapshot = [...items];
  listeners.forEach((fn) => fn(snapshot));
};

const persist = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* storage unavailable — keep in-memory only */
  }
  notify();
  window.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT));
};

// Re-sync when another store (e.g. CartProvider) writes to the same key.
window.addEventListener(CART_CHANGED_EVENT, () => {
  const next = load();
  if (JSON.stringify(next) === JSON.stringify(items)) return;
  items = next;
  notify();
});

/**
 * Add a landing course to the cart. `course` is a LANDING_COURSES entry.
 * Silently keeps the existing item when it is already in the cart (the
 * button never re-fires the toast for duplicates).
 */
export function addLandingCourseToCart(course) {
  if (!course?.id) return false;

  const exists = items.some(
    (item) => String(item.id) === String(course.id) && String(item.type || COURSE_TYPE) === COURSE_TYPE
  );
  if (exists) return false;

  items = [
    ...items,
    {
      id: course.id,
      type: COURSE_TYPE,
      title: course.title,
      instructor:
        course.instructor || course.resourcePersons?.map((person) => person?.name).filter(Boolean).join(", ") || "",
      thumbnail: course.image,
      category: course.category,
      duration:
        course.durationLabel || (course.hours ? formatDuration(course.hours * 3600) : ""),
      level: course.level,
      rating: course.ratingAverage ?? course.rating ?? null,
      price: course.price,
    },
  ];
  persist();
  window.dispatchEvent(
    new CustomEvent(CART_NOTIFY_EVENT, {detail: {message: `Added to cart · ${course.title}`}})
  );
  return true;
}

export function isLandingCourseInCart(id) {
  if (id === undefined || id === null) return false;
  return items.some(
    (item) => String(item.id) === String(id) && String(item.type || COURSE_TYPE) === COURSE_TYPE
  );
}

export function removeLandingCourseFromCart(id, type = COURSE_TYPE) {
  if (id === undefined || id === null) return;
  const next = items.filter(
    (item) => !(String(item.id) === String(id) && String(item.type || COURSE_TYPE) === String(type))
  );
  if (next.length === items.length) return;
  items = next;
  persist();
}

export function useLandingCartSync() {
  const [cart, setCart] = useState(() => [...items]);

  useEffect(() => {
    const fn = (next) => setCart(next);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  return {
    cart,
    isInCart: (id) =>
      cart.some(
        (item) => String(item.id) === String(id) && String(item.type || COURSE_TYPE) === COURSE_TYPE
      ),
    addCourse: addLandingCourseToCart,
    removeCourse: removeLandingCourseFromCart,
    count: cart.filter((item) => String(item.type || COURSE_TYPE) === COURSE_TYPE).length,
  };
}
